import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/table";
import { Heading } from "../components/heading";
import { Button } from "../components/button";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "../components/dialog";
import { Fieldset, Label } from "../components/fieldset";
import * as Headless from "@headlessui/react";
import { Input } from "../components/input";
import { Select } from "../components/select";
import { BSShowService } from "@/models/showService";
import { BSHorse } from "@/models/horse";
import { BSService } from "@/models/service";
import { collection, deleteDoc, doc, onSnapshot, query, setDoc, updateDoc, Timestamp, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { formatDate, formatDateToInputString } from "../helpers/date.helpers";
import { EllipsisHorizontalIcon, ChevronUpDownIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import { Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownMenu } from "../components/dropdown";
import { useTableSort } from '../hooks/useTableSort';
import { useColumnVisibility, ColumnConfig } from '../hooks/useColumnVisibility';
import { useTableSearch } from '../hooks/useTableSearch';
import { Switch, SwitchField } from "../components/switch";
import { Checkbox } from "../components/checkbox";
import { Alert, AlertDescription, AlertTitle, AlertActions } from "../components/alert";
import { InlineHorseSelect } from "./components/inline-horse-select";
import { InlineQuantityInput } from "./components/inline-quantity-input";

const InlineServiceSelect = ({ 
  show, 
  services, 
  onChange 
}: { 
  show: BSShowService, 
  services: BSService[], 
  onChange: (serviceId: string) => void 
}) => {
  return (
    <Select
      value={show.serviceId}
      onChange={(e) => onChange(e.target.value)}
      className="min-w-[200px]"
    >
      <option value="">Select a service...</option>
      {services
        .sort((a, b) => a.productServiceName.localeCompare(b.productServiceName))
        .map(service => (
          <option key={service.id} value={service.id}>
            {service.productServiceName}
          </option>
        ))}
    </Select>
  );
};

export default function Shows() {
  // Data states
  const [shows, setShows] = useState<BSShowService[]>([]);
  const [horses, setHorses] = useState<BSHorse[]>([]);
  const [services, setServices] = useState<BSService[]>([]);
  const [hideCompleted, setHideCompleted] = useState(true);

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Form state
  const emptyShow: Omit<BSShowService, 'id'> = {
    name: '',
    date: new Date(),
    horseId: '',
    customerId: '',
    serviceId: '',
    quantity: 1,
    billed: false
  };
  const [newShow, setNewShow] = useState(emptyShow);

  // Columns configuration
  const columns: ColumnConfig<BSShowService>[] = [
    { field: 'name', label: 'Show Name', defaultVisible: true },
    { field: 'date', label: 'Date', defaultVisible: true },
    { field: 'horseId', label: 'Horse', defaultVisible: true },
    { field: 'serviceId', label: 'Service', defaultVisible: true },
    { field: 'quantity', label: 'Quantity', defaultVisible: true },
    { field: 'billed', label: 'Billed', defaultVisible: true },
    { field: 'actions', label: 'Actions', defaultVisible: true },
  ];

  // Hooks
  const { sortField, sortDirection, handleSort, getSortedData } = useTableSort<BSShowService>('date');
  const { toggleColumn, isColumnVisible } = useColumnVisibility<BSShowService>(columns);
  const { searchQuery, setSearchQuery, getFilteredData } = useTableSearch<BSShowService>();

  // Add billing state
  const [isBilling, setIsBilling] = useState(false);
  const [isBillDialogOpen, setIsBillDialogOpen] = useState(false);

  // Fetch data
  useEffect(() => {
    const fetchShows = () => {
      const q = query(collection(db, "shows"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSShowService[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as BSShowService;
          const date = data.date instanceof Timestamp 
            ? data.date.toDate() 
            : data.date 
              ? new Date(data.date) 
              : null;
          items.push({ ...data, id: doc.id, date });
        });
        setShows(items);
      });
    };

    const fetchHorses = () => {
      const q = query(collection(db, "horses"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSHorse[] = [];
        querySnapshot.forEach((doc) => {
          items.push({ ...doc.data() as BSHorse, id: doc.id });
        });
        setHorses(items);
      });
    };

    const fetchServices = () => {
      const q = query(collection(db, "services"));
      onSnapshot(q, (querySnapshot) => {
        const items: BSService[] = [];
        querySnapshot.forEach((doc) => {
          items.push({ ...doc.data() as BSService, id: doc.id });
        });
        setServices(items);
      });
    };

    fetchShows();
    fetchHorses();
    fetchServices();
  }, []);

  // Move updateShowHorse inside component to access horses state
  const updateShowHorse = async (show: BSShowService, horseId: string) => {
    try {
      const horse = horses.find(h => h.id === horseId);
      if (!horse) return;

      await updateDoc(doc(db, 'shows', show.id), {
        horseId,
        customerId: horse.customerId
      });
    } catch (error) {
      console.error('Error updating show horse:', error);
    }
  };

  const updateShowService = async (show: BSShowService, serviceId: string) => {
    try {
      await updateDoc(doc(db, 'shows', show.id), {
        serviceId
      });
    } catch (error) {
      console.error('Error updating show service:', error);
    }
  };

  const updateShowQuantity = async (show: BSShowService, quantity: number) => {
    try {
      await updateDoc(doc(db, 'shows', show.id), {
        quantity
      });
    } catch (error) {
      console.error('Error updating show quantity:', error);
    }
  };

  // Actions
  const toggleBilled = async (show: BSShowService) => {
    try {
      await updateDoc(doc(db, 'shows', show.id), {
        billed: !show.billed
      });
    } catch (error) {
      console.error('Error toggling billed status:', error);
    }
  };

  const editShow = (show: BSShowService) => {
    setNewShow(show);
    setIsDialogOpen(true);
  };

  const deleteShow = async (show: BSShowService) => {
    await deleteDoc(doc(db, 'shows', show.id));
  };

  const resetForm = () => {
    setNewShow(emptyShow);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const selectedHorse = horses.find(h => h.id === newShow.horseId);
      if (!selectedHorse) throw new Error("Horse not found");

      const showData = {
        ...newShow,
        customerId: selectedHorse.customerId
      };

      if ('id' in newShow) {
        await updateDoc(doc(db, 'shows', newShow.id), showData);
      } else {
        const newDocRef = doc(collection(db, 'shows'));
        await setDoc(newDocRef, { ...showData, id: newDocRef.id });
      }

      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving show:', error);
    }
  };

  // Get filtered data
  const getFilteredShows = () => {
    let filtered = shows;
    
    if (hideCompleted) {
      filtered = filtered.filter(show => !show.billed);
    }

    filtered = getFilteredData(filtered, (show) => [
      show.name,
      horses.find(h => h.id === show.horseId)?.name || '',
      services.find(s => s.id === show.serviceId)?.productServiceName || '',
      show.date?.toLocaleDateString() || '',
      show.quantity.toString()
    ]);

    return getSortedData(filtered);
  };

  // Sortable header component
  const SortableHeader = ({ field, children }: { field: keyof BSShowService, children: React.ReactNode }) => {
    const isCurrent = sortField === field;
    return (
      <TableHeader 
        className="cursor-pointer select-none group hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <span className="inline-flex">
            {isCurrent ? (
              sortDirection === 'asc' ? (
                <ChevronUpIcon className="w-4 h-4" />
              ) : (
                <ChevronDownIcon className="w-4 h-4" />
              )
            ) : (
              <ChevronUpDownIcon className="w-4 h-4 opacity-0 group-hover:opacity-50" />
            )}
          </span>
        </div>
      </TableHeader>
    );
  };

  // Add billing function
  const billShows = async (shows: BSShowService[]) => {
    setIsBilling(true);
    const batch = writeBatch(db);

    try {
      for (const show of shows) {
        const horse = horses.find(h => h.id === show.horseId);
        const service = services.find(s => s.id === show.serviceId);

        if (!horse || !service) {
          console.error('Missing horse or service data');
          continue;
        }

        // Create bill item
        const billItem = {
          customerId: horse.customerId,
          horseId: show.horseId,
          serviceId: show.serviceId,
          date: new Date(),
          quantity: show.quantity,
          value: service.price * show.quantity,
          location: horse.location,
          exported: false
        };

        // Add bill item to batch
        const newBillRef = doc(collection(db, 'billItems'));
        batch.set(newBillRef, { ...billItem, id: newBillRef.id });

        // Update show billed status
        const showRef = doc(db, 'shows', show.id);
        batch.update(showRef, { billed: true });
      }

      await batch.commit();
      setIsBillDialogOpen(false);
    } catch (error) {
      console.error('Error billing shows:', error);
      // TODO: Add error handling
    } finally {
      setIsBilling(false);
    }
  };

  const duplicateShow = async (show: BSShowService) => {
    try {
      const { id, ...showWithoutId } = show;
      const newDocRef = doc(collection(db, 'shows'));
      await setDoc(newDocRef, { 
        ...showWithoutId,
        id: newDocRef.id,
      });
    } catch (error) {
      console.error('Error duplicating show:', error);
    }
  };

  return (
    <div>
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <Heading>Shows</Heading>
        <div className="flex items-center gap-4">
          <SwitchField>
            <Label>Showing {hideCompleted ? 'unbilled' : 'all'} shows</Label>
            <Switch color="green" checked={hideCompleted} onChange={setHideCompleted} />
          </SwitchField>
          <div className="relative">
            <Input
              type="search"
              placeholder="Search shows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[250px]"
            />
          </div>
          <Dropdown>
            <DropdownButton>
              <EyeIcon className="w-5 h-5 mr-2" />
              Columns
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              {columns.map(({ field, label }) => (
                <DropdownItem
                  key={field}
                  onClick={() => toggleColumn(field)}
                >
                  <div className="flex items-center gap-2">
                    {isColumnVisible(field) ? (
                      <EyeIcon className="w-4 h-4" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4" />
                    )}
                    {label}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
          >
            Add Show
          </Button>
        </div>
      </div>

      <Table
        dense
        striped
        className="[--gutter:theme(spacing.6)] sm:[--gutter:theme(spacing.8)]"
      >
        <TableHead>
          <TableRow>
            {columns.map(({ field, label }) => 
              isColumnVisible(field) && (
                field === 'actions' ? (
                  <TableHeader key={field}>Actions</TableHeader>
                ) : (
                  <SortableHeader key={field} field={field}>
                    {label}
                  </SortableHeader>
                )
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {getFilteredShows().map((show) => (
            <TableRow key={show.id}>
              {isColumnVisible('name') && (
                <TableCell>{show.name}</TableCell>
              )}
              {isColumnVisible('date') && (
                <TableCell>{formatDate(show.date)}</TableCell>
              )}
              {isColumnVisible('horseId') && (
                <TableCell>
                  <InlineHorseSelect
                    show={show}
                    horses={horses}
                    onChange={(horseId) => updateShowHorse(show, horseId)}
                  />
                </TableCell>
              )}
              {isColumnVisible('serviceId') && (
                <TableCell>
                  <InlineServiceSelect
                    show={show}
                    services={services}
                    onChange={(serviceId) => updateShowService(show, serviceId)}
                  />
                </TableCell>
              )}
              {isColumnVisible('quantity') && (
                <TableCell>
                  <InlineQuantityInput
                    show={show}
                    onChange={(quantity) => updateShowQuantity(show, quantity)}
                  />
                </TableCell>
              )}
              {isColumnVisible('billed') && (
                <TableCell>
                  <Checkbox
                    checked={show.billed}
                    onChange={() => toggleBilled(show)}
                  />
                </TableCell>
              )}
              {isColumnVisible('actions') && (
                <TableCell>
                  <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                    <Dropdown>
                      <DropdownButton plain aria-label="More options">
                        <EllipsisHorizontalIcon />
                      </DropdownButton>
                      <DropdownMenu anchor="bottom end">
                        <DropdownItem onClick={() => editShow(show)}>
                          Edit
                        </DropdownItem>
                        <DropdownItem onClick={() => duplicateShow(show)}>
                          Duplicate
                        </DropdownItem>
                        <DropdownDivider />
                        <DropdownItem onClick={() => deleteShow(show)}>
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex w-full flex-wrap items-center justify-between gap-4 border-t border-zinc-950/10 pt-6 dark:border-white/10">
        <div>
          <Button 
            onClick={() => {
              const unbilledShows = getFilteredShows().filter(show => !show.billed);
              if (unbilledShows.length > 0) {
                setIsBillDialogOpen(true);
              }
            }}
            disabled={isBilling || getFilteredShows().filter(show => !show.billed).length === 0}
          >
            {isBilling ? (
              <>
                <span className="inline-block animate-spin mr-2">⏳</span>
                Billing...
              </>
            ) : (
              `Bill ${getFilteredShows().filter(show => !show.billed).length} Shows`
            )}
          </Button>
        </div>
      </div>

      <Dialog 
        open={isDialogOpen} 
        onClose={setIsDialogOpen}
        className="w-full max-w-2xl"
      >
        <DialogTitle>{newShow.id ? 'Edit Show' : 'New Show'}</DialogTitle>
        <DialogBody>
          <form onSubmit={handleSubmit} className="w-full">
            <Fieldset className="w-full">
              <div className="grid grid-cols-1 items-center gap-x-4 gap-y-6 sm:grid-cols-1 w-full">
                <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                  <Label>Show Name</Label>
                  <Input
                    type="text"
                    value={newShow.name}
                    onChange={(e) => setNewShow({ ...newShow, name: e.target.value })}
                    required
                  />
                </Headless.Field>

                <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                  <Label>Date</Label>
                  <input
                    type="date"
                    value={formatDateToInputString(newShow.date)}
                    onChange={(e) => setNewShow({ 
                      ...newShow, 
                      date: new Date(`${e.target.value}T00:00:00`) 
                    })}
                    required
                  />
                </Headless.Field>

                <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                  <Label>Horse</Label>
                  <Select
                    value={newShow.horseId}
                    onChange={(e) => setNewShow({ ...newShow, horseId: e.target.value })}
                    required
                  >
                    <option value="">Select a horse...</option>
                    {horses
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(horse => (
                        <option key={horse.id} value={horse.id}>
                          {horse.name}
                        </option>
                      ))}
                  </Select>
                </Headless.Field>

                <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                  <Label>Service</Label>
                  <Select
                    value={newShow.serviceId}
                    onChange={(e) => setNewShow({ ...newShow, serviceId: e.target.value })}
                    required
                  >
                    <option value="">Select a service...</option>
                    {services
                      .sort((a, b) => a.productServiceName.localeCompare(b.productServiceName))
                      .map(service => (
                        <option key={service.id} value={service.id}>
                          {service.productServiceName}
                        </option>
                      ))}
                  </Select>
                </Headless.Field>

                <Headless.Field className="grid grid-cols-[subgrid] sm:col-span-3">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={newShow.quantity}
                    onChange={(e) => setNewShow({ ...newShow, quantity: parseInt(e.target.value) })}
                    required
                  />
                </Headless.Field>
              </div>
            </Fieldset>

            <DialogActions>
              <Button plain onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {newShow.id ? 'Save' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        </DialogBody>
      </Dialog>

      {/* Billing Confirmation Dialog */}
      <Alert 
        open={isBillDialogOpen} 
        onClose={() => !isBilling && setIsBillDialogOpen(false)}
      >
        <AlertTitle>Confirm Billing</AlertTitle>
        <AlertDescription>
          Are you sure you want to bill {getFilteredShows().filter(show => !show.billed).length} shows? 
          This action cannot be undone.
        </AlertDescription>
        <AlertActions>
          <Button 
            plain 
            onClick={() => setIsBillDialogOpen(false)}
            disabled={isBilling}
          >
            Cancel
          </Button>
          <Button
            onClick={() => billShows(getFilteredShows().filter(show => !show.billed))}
            disabled={isBilling}
          >
            {isBilling ? 'Billing...' : 'Confirm'}
          </Button>
        </AlertActions>
      </Alert>
    </div>
  );
}
